import { useState, useEffect } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import Input from '../input/Input';
import { toast } from 'react-toastify';
import {
  createAssetToIntegrate,
  updateAssetToIntegrate,
} from '../../services/GuaranteesService';

/** Fila seleccionada para edición; null/undefined = alta */
export interface AssetToIntegrateToEdit {
  id: number;
  name: string;
  description: string;
}

interface NewAssetsToIntegrateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  assetToEdit?: AssetToIntegrateToEdit | null;
}

const NewAssetsToIntegrateModal = ({
  isOpen,
  onClose,
  onSuccess,
  assetToEdit = null,
}: NewAssetsToIntegrateModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const isEditMode = Boolean(assetToEdit);

  const resetForm = () => {
    setName('');
    setDescription('');
  };

  useEffect(() => {
    if (!isOpen) return;
    if (assetToEdit) {
      setName(assetToEdit.name ?? '');
      setDescription(assetToEdit.description ?? '');
    } else {
      resetForm();
    }
  }, [isOpen, assetToEdit]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!name || name.trim() === '') {
      toast.error('Ingrese el nombre del activo');
      return;
    }

    if (!description || description.trim() === '') {
      toast.error('Ingrese la descripción del activo');
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = {
        name: name.trim(),
        description: description.trim(),
      };

      const response =
        isEditMode && assetToEdit
          ? await updateAssetToIntegrate(assetToEdit.id, requestBody)
          : await createAssetToIntegrate(requestBody);

      if (response.success) {
        toast.success(
          isEditMode
            ? 'Activo a integrar actualizado exitosamente'
            : 'Activo a integrar creado exitosamente'
        );
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMsg =
          'message' in response
            ? response.message
            : isEditMode
              ? 'Error al actualizar activo a integrar'
              : 'Error al crear activo a integrar';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error(
        isEditMode
          ? 'Error updating asset to integrate:'
          : 'Error creating asset to integrate:',
        error
      );
      toast.error(
        isEditMode
          ? 'Error al actualizar activo a integrar'
          : 'Error al crear activo a integrar'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Editar activo' : 'Añadir nuevo activo'}
      width='600px'
      primaryText={isEditMode ? 'Guardar' : 'Agregar'}
      primaryVariant='primary'
      onPrimaryAction={handleSubmit}
      primaryActionDisabled={isLoading}
      secondaryText='Cancelar'
      secondaryVariant='contained'
      onSecondaryAction={handleClose}
      showOverlay={true}
    >
      <Card margin={{ top: '4' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          <Input
            label="Nombre *"
            name="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLoading}
            size="md"
          />

          <Input
            label="Descripción *"
            name="description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isLoading}
            size="md"
          />
        </div>
      </Card>
    </Modal>
  );
};

export default NewAssetsToIntegrateModal;
